import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Linkedin } from "lucide-react";
import { Reveal } from "../components/motion";
import { Seo } from "../components/Seo";
import { PROFILE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 .'-]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^[+]?[0-9()\s-]{6,18}$/;

const initial = { name: "", email: "", mobile: "", message: "" };

// Pure validators (module scope -> order-safe, no re-creation per render)
const validateField = (name, value) => {
  switch (name) {
    case "name":
      if (!value.trim()) return "Name is required.";
      if (!NAME_RE.test(value.trim())) return "Only letters, numbers, spaces, . ' - allowed.";
      return "";
    case "email":
      if (!value.trim()) return "Email is required.";
      if (!EMAIL_RE.test(value.trim())) return "Enter a valid email address.";
      return "";
    case "message":
      if (!value.trim()) return "Message is required.";
      if (value.trim().length < 10) return "Please add a little more detail (min 10 characters).";
      return "";
    case "mobile":
      if (value && !MOBILE_RE.test(value)) return "Enter a valid mobile number.";
      return "";
    default:
      return "";
  }
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const isFormValid = useMemo(() => {
    const requiredOk = ["name", "email", "message"].every((k) => !validateField(k, form[k]));
    const mobileOk = !validateField("mobile", form.mobile);
    return requiredOk && mobileOk;
  }, [form]);

  const validateAll = () => {
    const next = {};
    ["name", "email", "message", "mobile"].forEach((k) => {
      const e = validateField(k, form[k]);
      if (e) next[k] = e;
    });
    setErrors(next);
    setTouched({ name: true, email: true, message: true, mobile: true });
    return Object.keys(next).length === 0;
  };

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (touched[name]) setErrors((e) => ({ ...e, [name]: validateField(name, value) }));
  };

  const onBlur = (e) => {
    const { name, value } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((er) => ({ ...er, [name]: validateField(name, value) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.email_sent ? "Message delivered. I'll be in touch shortly." : "Message received. I'll be in touch shortly.");
      setForm(initial);
      setErrors({});
      setTouched({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const errStyle = (name) => ({ borderColor: errors[name] ? "#ef4444" : "var(--line)", color: "var(--text)" });
  const star = <span style={{ color: "var(--accent)" }}> *</span>;
  const renderError = (name) => errors[name] ? (
    <span className="mt-2 block text-xs" style={{ color: "#ef4444" }} data-testid={`error-${name}`}>{errors[name]}</span>
  ) : null;
  const labelCls = "text-xs uppercase tracking-widest";

  return (
    <div data-testid="contact-page">
      <Seo path="/contact" description="Get in touch with Yuva Kumar — UX Director & AI-Native Product Designer. Let's discuss UX strategy, design systems, design ops or AI-native product design for your organization." keywords="contact Yuva Kumar, hire UX Director, UX consulting, design leadership contact" type="website" />
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal><p className="eyebrow mb-6">Contact</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="display text-5xl sm:text-8xl max-w-[16ch]" style={{ color: "var(--text)" }}>Let&apos;s talk business.</h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid lg:grid-cols-[1.3fr_1fr] gap-16">
          <Reveal>
            <form onSubmit={onSubmit} noValidate className="space-y-8" data-testid="contact-form">
              {/* Name + Email */}
              <div className="grid sm:grid-cols-2 gap-8">
                <label className="block">
                  <span className={labelCls} style={{ color: "var(--muted-t)" }}>Name{star}</span>
                  <input
                    name="name" value={form.name} onChange={(e) => setField("name", e.target.value)} onBlur={onBlur}
                    placeholder="e.g. Priya Sharma" data-testid="input-name"
                    className="mt-3 w-full bg-transparent border-b py-3 outline-none transition-colors text-lg"
                    style={errStyle("name")}
                  />
                  {renderError("name")}
                </label>

                <label className="block">
                  <span className={labelCls} style={{ color: "var(--muted-t)" }}>Email{star}</span>
                  <input
                    name="email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} onBlur={onBlur}
                    placeholder="e.g. priya@company.com" data-testid="input-email"
                    className="mt-3 w-full bg-transparent border-b py-3 outline-none transition-colors text-lg"
                    style={errStyle("email")}
                  />
                  {renderError("email")}
                </label>
              </div>

              {/* Mobile */}
              <label className="block">
                <span className={labelCls} style={{ color: "var(--muted-t)" }}>Mobile Number</span>
                <input
                  name="mobile" value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} onBlur={onBlur}
                  placeholder="e.g. +91 98765 43210" data-testid="input-mobile"
                  className="mt-3 w-full bg-transparent border-b py-3 outline-none transition-colors text-lg"
                  style={errStyle("mobile")}
                />
                {renderError("mobile")}
              </label>

              {/* Message */}
              <label className="block">
                <span className={labelCls} style={{ color: "var(--muted-t)" }}>Message{star}</span>
                <textarea
                  name="message" rows={5} value={form.message} onChange={(e) => setField("message", e.target.value)} onBlur={onBlur}
                  placeholder="write your message here..." data-testid="input-message"
                  className="mt-3 w-full bg-transparent border-b py-3 outline-none resize-none transition-colors text-lg"
                  style={errStyle("message")}
                />
                {renderError("message")}
              </label>

              <button
                type="submit" disabled={loading || !isFormValid} data-testid="submit-contact"
                className="group inline-flex items-center gap-4 px-9 py-5 rounded-full transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "var(--accent)", color: "var(--bg)" }}
                title={isFormValid ? "" : "Fill in name, a valid email and your message to enable"}
              >
                <span className="text-sm uppercase tracking-widest">{loading ? "Sending..." : "Send Message"}</span>
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="surface-card p-10">
              <p className="eyebrow mb-8">Direct lines</p>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <Mail size={18} style={{ color: "var(--accent)" }} className="mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Email</p>
                    <a href={`mailto:${PROFILE.email}`} data-testid="contact-email" className="text-lg hover:opacity-70 transition-opacity" style={{ color: "var(--text)" }}>{PROFILE.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={18} style={{ color: "var(--accent)" }} className="mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Location</p>
                    <p className="text-lg" style={{ color: "var(--text)" }}>{PROFILE.location}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Linkedin size={18} style={{ color: "var(--accent)" }} className="mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>LinkedIn</p>
                    <a href={PROFILE.linkedin} target="_blank" rel="noreferrer" className="text-lg hover:opacity-70 transition-opacity" style={{ color: "var(--text)" }}>linkedin.com/in/yuvap</a>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
